import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { extractErrorMessage, RecaptchaService } from '@crowdctrl/shared';
import {
  PublicFormIntakeService,
  type PublicOrderFollowContextResponse,
} from '../services/public-form-intake.service';

export interface PublicOrderFollowResolvedData {
  followUrlDocId: string;
  context: PublicOrderFollowContextResponse | null;
  errorMessage: string | null;
}

/**
 * Same pattern as `publicFormContextResolver` (public-form-context.resolver.ts): reCAPTCHA then
 * server context. Action `public_form_context` matches `/s/:shortId` public forms.
 */
export const publicOrderFollowResolver: ResolveFn<PublicOrderFollowResolvedData> = async (
  route,
) => {
  const recaptcha = inject(RecaptchaService);
  const intake = inject(PublicFormIntakeService);

  const followUrlDocId = route.paramMap.get('followId')?.trim() ?? '';

  if (!followUrlDocId) {
    return {
      followUrlDocId: '',
      context: null,
      errorMessage: 'Missing follow link.',
    };
  }

  if (!recaptcha.isConfigured()) {
    return {
      followUrlDocId,
      context: null,
      errorMessage:
        'This page cannot be loaded. Check that the API URL and reCAPTCHA site key are configured.',
    };
  }

  try {
    const token = await recaptcha.execute('public_form_context');
    const context = await intake.fetchOrderFollowContext(followUrlDocId, token);
    return {
      followUrlDocId,
      context,
      errorMessage: null,
    };
  } catch (error) {
    return {
      followUrlDocId,
      context: null,
      errorMessage: extractErrorMessage(error) || 'Failed to load order',
    };
  }
};
